/**
 * `page_view` events for client-side navigations.
 *
 * Gatsby changes pages without a reload, so Google Tag Manager sees one page
 * load per visit unless the site reports the rest. This pushes one GA4-shaped
 * `page_view` per navigation into the dataLayer. It is wired from
 * `gatsby-browser.js`.
 *
 * Two page views are counted by GTM's own Google tag, not by this module: the
 * initial load, and the page the visitor is on when analytics consent is
 * granted (the Google tag fires on that consent event and sends a page view of
 * its own). `gatsby-browser.js` reports both with {@link PageViewTracker.markCounted}
 * so that the next route update on the same path — an anchor link, a hash
 * change — is not pushed as a second view.
 *
 * A dataLayer event is inert until the GTM container has a trigger for it.
 * Container `GTM-5PKXQKHR` needs a Custom Event trigger named `page_view` on
 * the GA4 event tag; that lives in GTM, not in this repository (docs/gtm/).
 */

export const PAGE_VIEW_EVENT = 'page_view';

export interface PageViewLocation {
  pathname: string;
  search: string;
  href: string;
}

export interface PageViewEvent {
  event: typeof PAGE_VIEW_EVENT;
  page_path: string;
  page_location: string;
}

interface PageViewTrackerDeps {
  /** Whether the visitor has consented to analytics cookies right now. */
  hasConsent: () => boolean;
  /** The dataLayer to push into, resolved at call time so it can be created lazily. */
  dataLayer: () => unknown[];
}

export interface PageViewTracker {
  /**
   * Records a page view for `location`, or declines. Declines without consent
   * (and without remembering the path, so the view counts once consent
   * arrives), and for a path equal to the last one recorded or marked, which is
   * what an anchor link or a hash change produces. Returns whether an event was
   * pushed.
   */
  track: (location: PageViewLocation) => boolean;
  /**
   * Notes that `location` has just been counted by something else — GTM's
   * Google tag on the initial load or on consent — so `track` will not push it
   * again until the visitor has been elsewhere. Pushes nothing.
   */
  markCounted: (location: PageViewLocation) => void;
}

const pathOf = (location: PageViewLocation): string =>
  `${location.pathname}${location.search}`;

export const createPageViewTracker = ({
  hasConsent,
  dataLayer,
}: PageViewTrackerDeps): PageViewTracker => {
  let lastPath: string | null = null;

  return {
    markCounted: (location) => {
      lastPath = pathOf(location);
    },

    track: (location) => {
      if (!hasConsent()) {
        return false;
      }

      const path = pathOf(location);

      if (path === lastPath) {
        return false;
      }

      const event: PageViewEvent = {
        event: PAGE_VIEW_EVENT,
        page_path: path,
        page_location: location.href,
      };

      dataLayer().push(event);
      lastPath = path;

      return true;
    },
  };
};
